/**
 * @fileoverview `<Progress>` — barre de progression accessible.
 *
 * Utilisée pour les flux bornés (upload, import, génération d'export) et les
 * étapes de polling quand une estimation de progression est disponible.
 */

import { cn } from '../lib/utils.ts';

export interface ProgressProps {
  /** Valeur courante. Bornée entre 0 et `max`. */
  value: number;
  /** Valeur maximale. Défaut : 100. */
  max?: number;
  /** Libellé accessible. */
  ariaLabel?: string;
  /** Affiche le pourcentage à droite. */
  showValue?: boolean;
  /** Texte optionnel au-dessus de la barre. */
  label?: string;
  className?: string;
}

/**
 * Barre de progression GNPC.
 *
 * @param props - Valeur courante, maximum, label accessible et affichage
 * optionnel du pourcentage.
 * @returns Barre `role="progressbar"` bornée entre 0 et `max`.
 *
 * @example
 * ```tsx
 * <Progress label="Export en préparation" value={62} showValue />
 * ```
 */
export function Progress({
  value,
  max = 100,
  ariaLabel = 'Progression',
  showValue = false,
  label,
  className,
}: ProgressProps): React.JSX.Element {
  const boundedMax = Math.max(1, max);
  const boundedValue = Math.min(Math.max(value, 0), boundedMax);
  const percent = Math.round((boundedValue / boundedMax) * 100);

  return (
    <div className={cn('w-full space-y-2', className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between gap-3 text-sm">
          {label && <span className="text-quipux-navy-950 font-medium">{label}</span>}
          {showValue && (
            <span className="text-muted-foreground font-mono text-xs tabular-nums">{percent}%</span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={boundedMax}
        aria-valuenow={boundedValue}
        className="bg-muted h-2 w-full overflow-hidden rounded-full"
      >
        <div
          className="bg-primary duration-normal h-full rounded-full transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
